import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { Btn, PageHead } from "@/admin/AdminLayout";
import { ReplyTemplates } from "@/admin/ReplyTemplates";

const TITLES = { inquiry: "Vehicle Requests", sourcing: "Sourcing Requests", sell: "Sell Your Vehicle Requests", contact: "Messages" };
const STATUSES = ["new", "contacted", "qualified", "closed", "archived"];
const Row = ({ k, v }) => (v || v === 0) && String(v).trim() !== "" ? <div className="grid grid-cols-[9rem_1fr] gap-3 border-b border-white/5 py-2 text-sm"><span className="mono text-[0.55rem] text-[#8b95a5]">{k.replace(/_/g, " ")}</span><span className="break-words">{Array.isArray(v) ? v.join(", ") : String(v)}</span></div> : null;

export default function Leads() {
  const { type } = useParams();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);
  const [notes, setNotes] = useState("");
  const { data = [] } = useQuery({ queryKey: ["admin-leads", type, status], queryFn: async () => (await api.get("/admin/leads", { params: { type, status } })).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-leads"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };
  const patch = useMutation({ mutationFn: ({ id, body }) => api.patch(`/admin/leads/${id}`, body), onSuccess: () => { toast.success("Lead updated"); refresh(); } });
  const remove = useMutation({ mutationFn: (id) => api.delete(`/admin/leads/${id}`), onSuccess: () => { setSel(null); refresh(); } });
  const open = (l) => { setSel(l); setNotes(l.admin_notes || ""); };
  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/leads/export", { params: { type, status }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `pmg-leads-${type || "all"}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel export downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="admin-leads">
      <PageHead title={TITLES[type] || "Leads"}>
        <Btn variant="ghost" onClick={exportXlsx} data-testid="leads-export"><Download size={13} /> EXPORT EXCEL</Btn>
        <select className="field !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-status-filter">
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </PageHead>
      <div className="admin-card overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="mono text-[0.55rem] text-[#8b95a5]"><tr>{["Date", "Name", "Contact", "Vehicle", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {data.map((l) => (
              <tr key={l.id} className="cursor-pointer border-t border-white/5 hover:bg-white/5" onClick={() => open(l)} data-testid={`lead-row-${l.id}`}>
                <td className="whitespace-nowrap px-4 py-3 text-[#8b95a5]">{l.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 font-medium">{l.name}<div className="text-xs text-[#8b95a5]">{l.country}</div></td>
                <td className="px-4 py-3 text-xs"><div>{l.email}</div><div className="text-[#8b95a5]">{l.phone || l.whatsapp}</div></td>
                <td className="max-w-[16rem] truncate px-4 py-3">{l.vehicle || l.message?.slice(0, 60)}</td>
                <td className="px-4 py-3"><span className={`mono text-[0.55rem] ${l.status === "new" ? "text-[#e2c89d]" : "text-[#8b95a5]"}`}>{l.status}</span></td>
                <td className="px-4 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); window.confirm("Delete lead?") && remove.mutate(l.id); }} className="text-[#8b95a5] hover:text-red-400"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {!data.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8b95a5]">No leads.</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setSel(null)}>
          <div className="h-full w-full max-w-lg overflow-y-auto border-l border-white/10 bg-[#0b0d10] p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-detail">
            <div className="flex items-start justify-between"><div><p className="mono text-[0.55rem] text-[#8b95a5]">{sel.type} · {sel.created_at?.slice(0, 16).replace("T", " ")}</p><h2 className="display-tight mt-2 text-xl">{sel.name}</h2></div><button onClick={() => setSel(null)}><X /></button></div>
            <div className="mt-6">
              {["email", "phone", "whatsapp", "country", "vehicle", "message"].map((k) => <Row key={k} k={k} v={sel[k]} />)}
              {Object.entries(sel.data || {}).map(([k, v]) => <Row key={k} k={k} v={v} />)}
            </div>
            {sel.photos?.length > 0 && <div className="mt-5 grid grid-cols-3 gap-2">{sel.photos.map((p) => <a key={p} href={mediaUrl(p)} target="_blank" rel="noreferrer"><img src={mediaUrl(p)} alt="" className="aspect-square w-full object-cover" /></a>)}</div>}
            <div className="mt-8 space-y-4">
              <label className="block"><span className="mb-1.5 block text-xs text-[#8b95a5]">Status</span>
                <select className="field" value={sel.status} onChange={(e) => { setSel({ ...sel, status: e.target.value }); patch.mutate({ id: sel.id, body: { status: e.target.value } }); }} data-testid="lead-status-select">{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
              <label className="block"><span className="mb-1.5 block text-xs text-[#8b95a5]">Admin notes</span><textarea className="field" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes" /></label>
              <div className="flex gap-2">
                <Btn onClick={() => patch.mutate({ id: sel.id, body: { admin_notes: notes } })} data-testid="lead-save-notes">SAVE NOTES</Btn>
                {sel.email && <a href={`mailto:${sel.email}`} className="btn btn-ghost !py-2 !px-3.5 !text-[0.62rem]">EMAIL</a>}
                {(sel.whatsapp || sel.phone) && <a href={`https://wa.me/${(sel.whatsapp || sel.phone).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="btn btn-ghost !py-2 !px-3.5 !text-[0.62rem]">WHATSAPP</a>}
              </div>
            </div>
            <ReplyTemplates lead={sel} />
          </div>
        </div>
      )}
    </div>
  );
}
